import { motion } from "framer-motion";
import { Wallet, Zap, Users, Activity, Shield, ArrowRight, Clock, CheckCircle2, ArrowUpRight, ArrowDownRight, FileText, CreditCard, RefreshCw, Lock } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const wallets = [
  { name: "Energy Wallet", icon: Zap, desc: "Accumulates daily performance-based distributions from your active energy packages. Funds are generated from actual power grid revenue.", balance: "$12,450.00", color: "from-primary to-accent" },
  { name: "Referral Wallet", icon: Users, desc: "Earns commissions when your direct and indirect referrals activate energy packages. Up to 5 levels of referral bonuses.", balance: "$5,230.40", color: "from-primary to-primary" },
  { name: "Binary Wallet", icon: Activity, desc: "Receives matching bonuses from your binary team structure. Calculated from the weaker leg's volume each cycle.", balance: "$4,120.20", color: "from-accent to-primary" },
  { name: "Bonus Wallet", icon: Shield, desc: "Collects rank achievement bonuses, target completion rewards, and special promotional incentives.", balance: "$2,050.00", color: "from-primary to-accent" },
  { name: "Withdrawal Wallet", icon: Wallet, desc: "Your consolidated withdrawal-ready balance. Transfer funds from other wallets here to initiate payouts.", balance: "$1,000.00", color: "from-accent to-primary" },
];

const transactions = [
  { type: "Energy Distribution", amount: "+$145.00", wallet: "Energy", date: "Today, 14:32", status: "Completed", up: true },
  { type: "Level 1 Referral", amount: "+$90.00", wallet: "Referral", date: "Today, 09:15", status: "Completed", up: true },
  { type: "Withdrawal", amount: "-$500.00", wallet: "Withdrawal", date: "Yesterday", status: "Processing", up: false },
  { type: "Binary Match", amount: "+$210.00", wallet: "Binary", date: "Yesterday", status: "Completed", up: true },
  { type: "Rank Bonus", amount: "+$250.00", wallet: "Bonus", date: "Mon, 20 Feb", status: "Completed", up: true },
  { type: "Energy Distribution", amount: "+$145.00", wallet: "Energy", date: "Mon, 20 Feb", status: "Completed", up: true },
  { type: "Withdrawal", amount: "-$1,000.00", wallet: "Withdrawal", date: "Sun, 19 Feb", status: "Completed", up: false },
];

const WalletPayout = () => (
  <div>
    <PageHero
      eyebrow="Wallet System"
      title={<>Transparent <span className="energy-gradient-text">Earnings Management</span></>}
      description="Five specialized wallets keep your earnings organized and transparent. Every transaction is traceable, every distribution is documented."
      backgroundImage={heroBg}
    />

    {/* Wallet Cards */}
    <Section>
      <SectionHeading eyebrow="Your Wallets" title="Five-Wallet Architecture" description="Each wallet serves a distinct purpose, ensuring complete transparency and audit trail for all earnings." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {wallets.map((w, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/0 via-primary/40 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl energy-gradient flex items-center justify-center group-hover:shadow-[0_0_20px_hsl(155_80%_50%/0.3)] transition-shadow">
                <w.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-base font-bold">{w.name}</h3>
                <div className="text-2xl font-bold text-primary glow-text mt-1">{w.balance}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{w.desc}</p>
          </motion.div>
        ))}
        {/* Total balance card */}
        <motion.div {...fadeUp} transition={{ delay: 0.5 }} className="p-6 rounded-2xl glass-card border-primary/30 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 rounded-full energy-gradient flex items-center justify-center mb-3 animate-glow-pulse">
            <CreditCard className="w-7 h-7 text-primary-foreground" />
          </div>
          <div className="text-xs text-muted-foreground mb-1">Total Portfolio Value</div>
          <div className="font-display text-3xl font-bold text-primary glow-text">$24,850.60</div>
        </motion.div>
      </div>
    </Section>

    {/* Transaction History */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Transaction Log" title="Recent Activity" description="Every transaction is recorded and timestamped for complete financial transparency." />
        <motion.div {...fadeUp} className="max-w-3xl mx-auto space-y-2">
          {transactions.map((tx, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.06 }} className="flex items-center gap-4 p-4 rounded-xl glass-card hover:bg-secondary/30 transition-colors">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tx.up ? "bg-primary/10" : "bg-destructive/10"}`}>
                {tx.up ? <ArrowUpRight className="w-4 h-4 text-primary" /> : <ArrowDownRight className="w-4 h-4 text-destructive" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{tx.type}</span>
                  <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-secondary/50">{tx.wallet}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">{tx.date}</div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-bold ${tx.up ? "text-primary" : "text-destructive"}`}>{tx.amount}</div>
                <div className={`text-[10px] ${tx.status === "Completed" ? "text-primary" : "text-accent"}`}>{tx.status}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>

    {/* Withdrawal Features */}
    <Section>
      <SectionHeading eyebrow="Payouts" title="Withdrawal System" description="Simple, secure, and fast — withdraw your earnings with full transparency." />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Clock, title: "Weekly Withdrawals", desc: "Process withdrawal requests every week on designated payout days." },
          { icon: FileText, title: "Auto Statements", desc: "Downloadable monthly statements with complete transaction history." },
          { icon: RefreshCw, title: "Processing Fees", desc: "Transparent fee structure displayed before every withdrawal confirmation." },
          { icon: Lock, title: "Secure Processing", desc: "Multi-layer verification and encrypted payment channels for all payouts." },
        ].map((f, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="text-center p-6 rounded-2xl glass-card glass-card-hover group transition-all duration-500">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
              <f.icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-display text-sm font-semibold mb-2">{f.title}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  </div>
);

export default WalletPayout;
